import { useState, useEffect, useMemo } from 'react'
import { usePortalClient } from '../hooks/usePortalClient'
import { supabase } from '../../../shared/lib/supabase'
import { calculateScores, calculateRiskMaturityAlignment, calculateBenchmarkGap, getEnhancedGapAnalysis } from '../../../shared/utils/scoring'
import { calculateTAPS } from '../../../shared/utils/threatScoring'
import { calculateLossScenarios } from '../../../shared/utils/impactCalibration'
import { generateRoadmap } from '../../../shared/utils/roadmapGenerator'
import threatProfiles from '../../../shared/data/threatProfiles.json'
import { generateEnablementPlan, generateExecutiveNarrative } from '../../../shared/utils/enablementPlanGenerator'
import { calculateInsuranceAdequacy } from '../../../shared/utils/insuranceAdequacy'
import { getEngagementDriverContext } from '../../../shared/utils/engagementDriver'
import { getMarketSegmentId } from '../../../shared/utils/marketSegment'

const QUADRANT_STYLES = {
  'quick-win': { label: 'Quick Win', color: '#22C55E', bg: '#F0FDF4' },
  'strategic-investment': { label: 'Strategic', color: '#3B82F6', bg: '#EFF6FF' },
  'efficiency-play': { label: 'Efficiency', color: '#F59E0B', bg: '#FFFBEB' },
  'defer-accept': { label: 'Defer', color: '#94A3B8', bg: '#F1F5F9' }
}

const PRIORITY_COLORS = {
  Critical: '#EF4444',
  High: '#F59E0B',
  Medium: '#3B82F6',
  Low: '#94A3B8'
}

function PortalPlan() {
  const { client, clientId, loading: clientLoading } = usePortalClient()
  const [assessmentData, setAssessmentData] = useState(null)
  const [enablementConfig, setEnablementConfig] = useState(null)
  const [insuranceReview, setInsuranceReview] = useState(null)
  const [dataLoading, setDataLoading] = useState(true)
  const [planView, setPlanView] = useState('phases')

  // Fetch latest completed assessment and enablement config
  useEffect(() => {
    if (!clientId) return

    const fetchData = async () => {
      setDataLoading(true)
      try {
        // Fetch latest completed assessment with responses and scores
        const { data: assessments } = await supabase
          .from('assessments')
          .select('id, status, completed_at, tida_enabled')
          .eq('client_id', clientId)
          .eq('status', 'completed')
          .order('completed_at', { ascending: false })
          .limit(1)

        if (assessments?.[0]) {
          const assessmentId = assessments[0].id
          const { data: responses } = await supabase
            .from('assessment_responses')
            .select('risk_appetite, business_scoping, technology_scoping, maturity_assessment, notes')
            .eq('assessment_id', assessmentId)
            .single()

          const { data: scoresRow } = await supabase
            .from('assessment_scores')
            .select('overall_score, maturity_level, category_scores, risk_appetite_scores, business_scoping_scores')
            .eq('assessment_id', assessmentId)
            .single()

          if (responses && scoresRow) {
            setAssessmentData({
              id: assessmentId,
              tida_enabled: assessments[0].tida_enabled,
              responses: {
                riskAppetite: responses.risk_appetite,
                businessScoping: responses.business_scoping,
                technologyScoping: responses.technology_scoping,
                maturityAssessment: responses.maturity_assessment,
                notes: responses.notes
              },
              scores: {
                overall: { score: scoresRow.overall_score, maturityLevel: scoresRow.maturity_level },
                maturity: { categoryScores: scoresRow.category_scores || {} },
                riskAppetite: scoresRow.risk_appetite_scores || {}
              }
            })
          }
        }

        // Fetch enablement config (may not exist yet)
        const { data: config } = await supabase
          .from('client_enablement_config')
          .select('*')
          .eq('client_id', clientId)
          .maybeSingle()

        if (config) setEnablementConfig(config)

        // Fetch latest insurance review so insurance initiatives surface
        // alongside security ones in the Enablement Plan.
        const { data: reviews } = await supabase
          .from('client_insurance_policy_reviews')
          .select('id, coverage_limit_aggregate, retention, sublimits, notable_exclusions, findings, reviewed_at')
          .eq('client_id', clientId)
          .order('reviewed_at', { ascending: false })
          .limit(1)
        if (reviews?.[0]) setInsuranceReview(reviews[0])
      } catch (err) {
        console.error('Error fetching enablement plan data:', err)
      }
      setDataLoading(false)
    }

    fetchData()
  }, [clientId])

  // Compute enablement plan from assessment data
  const { plan, narrative } = useMemo(() => {
    if (!assessmentData?.responses) return { plan: null, narrative: null }

    try {
      const { responses } = assessmentData
      // Always compute full scores from responses — the DB-cached scores
      // are a sparse summary missing fields the pipeline needs
      const scores = calculateScores(responses)
      const industry = responses?.businessScoping?.['bs-0']?.value || client?.industry || 'Other'
      const marketSegment = getMarketSegmentId(responses?.businessScoping)

      const benchmarkData = calculateBenchmarkGap(scores.overall.score, industry, marketSegment)
      const alignments = calculateRiskMaturityAlignment(scores.riskAppetite, scores.maturity)
      const gapAnalysis = getEnhancedGapAnalysis(alignments, industry, responses?.businessScoping)
      const engagementContext = getEngagementDriverContext(responses?.businessScoping)

      // Compute TAPS prioritization
      const threatContext = responses?.notes?.threatContext || responses?.threatContext || null
      const rawImpactData = responses?.notes?.impactData || responses?.impactData || null
      // Overlay the latest cyber Insurance Policy Review onto impact data.
      let impactData = rawImpactData
      if (rawImpactData && insuranceReview) {
        const reviewLimit = parseFloat(insuranceReview.coverage_limit_aggregate) || 0
        impactData = {
          ...rawImpactData,
          insurance_coverage_limit: reviewLimit > 0 ? reviewLimit : rawImpactData.insurance_coverage_limit,
          insuranceCoverageLimit: reviewLimit > 0 ? reviewLimit : rawImpactData.insuranceCoverageLimit,
          client_retention: insuranceReview.retention || rawImpactData.client_retention,
          deductible: insuranceReview.retention || rawImpactData.deductible,
          has_cyber_insurance: reviewLimit > 0 ? true : rawImpactData.has_cyber_insurance,
          insuranceSublimits: insuranceReview.sublimits || {},
          insuranceExclusions: insuranceReview.notable_exclusions || [],
          insuranceReviewFindings: insuranceReview.findings || null,
          insuranceReviewId: insuranceReview.id,
          insuranceReviewedAt: insuranceReview.reviewed_at
        }
      }
      const profileKey = threatContext?.profileKey || 'TP-DEFAULT'
      const selectedProfile = threatProfiles.profiles.find(p => p.verticalKey === profileKey)
        || threatProfiles.profiles.find(p => p.verticalKey === 'TP-DEFAULT')

      const tapsResults = calculateTAPS(
        gapAnalysis,
        {
          profile_key: profileKey,
          threat_actor_weights: threatContext?.threatActorWeights || {},
          attack_vector_weights: threatContext?.attackVectorWeights || {},
          target_asset_weights: threatContext?.assetWeights || {}
        },
        impactData || {},
        selectedProfile,
        engagementContext
      )

      const roadmap = generateRoadmap(
        tapsResults,
        impactData || {},
        gapAnalysis,
        responses?.technologyScoping || null,
        engagementContext,
        responses?.businessScoping || null
      )

      // Loss scenarios (only if TIDA data exists)
      let lossScenariosList = []
      if (impactData && threatContext) {
        try {
          const result = calculateLossScenarios(
            impactData,
            scores.overall.score,
            threatContext.profileKey,
            responses?.businessScoping
          )
          lossScenariosList = result?.scenarios || []
        } catch {
          // TIDA data may be incomplete
        }
      }

      // Compute insurance adequacy from review-overlaid impactData
      const insuranceAdequacy = impactData
        ? calculateInsuranceAdequacy(impactData, { scenarios: lossScenariosList }, {})
        : null

      const enablementPlan = generateEnablementPlan({
        roadmap,
        gapAnalysis,
        lossScenarios: lossScenariosList,
        tapsResults: tapsResults,
        scores,
        threatContext: threatContext || {},
        businessScopingResponses: responses?.businessScoping || {},
        insuranceAdequacy,
        config: enablementConfig || {}
      })

      const execNarrative = generateExecutiveNarrative(enablementPlan)

      return { plan: enablementPlan, narrative: execNarrative }
    } catch (err) {
      console.error('Enablement plan computation error:', err)
      return { plan: null, narrative: null }
    }
  }, [assessmentData, enablementConfig, client, insuranceReview])

  // Published AI content (admin-approved)
  const publishedAI = useMemo(() => {
    if (!enablementConfig?.ai_published_at) return null
    const initiativeMap = {}
    if (enablementConfig.ai_initiatives_published) {
      enablementConfig.ai_initiatives_published.forEach(i => {
        initiativeMap[i.id] = { recommendation: i.recommendation, coiExplanation: i.coiExplanation }
      })
    }
    return {
      narrative: enablementConfig.ai_narrative_published,
      initiativeMap
    }
  }, [enablementConfig])

  const showCOI = enablementConfig?.show_cost_of_inaction !== false

  if (clientLoading || dataLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
      </div>
    )
  }

  // Empty state — no assessment or no plan
  if (!plan) {
    return (
      <div className="space-y-5">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">Enablement Plan Coming Soon</h3>
          <p className="text-sm mt-2 max-w-md mx-auto text-gray-500 dark:text-gray-400">
            Your advisor is finalizing your security assessment. Once complete, your personalized enablement plan will appear here with prioritized initiatives and recommendations.
          </p>
        </div>
      </div>
    )
  }

  const totalInitiatives = plan.summary.totalInitiatives

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Strategic Security Enablement Plan</h2>
            <p className="text-xs mt-1 leading-relaxed text-gray-500 dark:text-gray-400">
              {publishedAI?.narrative?.headline || narrative?.headline || `${totalInitiatives} prioritized initiatives across ${plan.phases.length} phases`}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {['phases', 'priority'].map(view => (
              <button
                key={view}
                onClick={() => setPlanView(view)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-md capitalize border ${
                  planView === view
                    ? 'border-primary-400 bg-primary-500/10 text-gray-700 dark:text-gray-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                }`}
              >
                {view}
              </button>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <div className={`grid gap-3 mt-4 ${showCOI ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">Quick Wins</p>
            <p className="text-xl font-bold text-green-500">{plan.summary.quadrantDistribution['quick-win'] || 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">Strategic</p>
            <p className="text-xl font-bold text-blue-500">{plan.summary.quadrantDistribution['strategic-investment'] || 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Initiatives</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{totalInitiatives}</p>
          </div>
          {showCOI && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-xs text-gray-500 dark:text-gray-400">Annual Exposure</p>
              <p className="text-xl font-bold text-red-500">{plan.summary.totalCostOfInactionFormatted}</p>
            </div>
          )}
        </div>
      </div>

      {/* Phase cards */}
      {planView === 'phases' && plan.phases.map(phase => (
        <div key={phase.id} className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
            <div>
              <h3 className="text-sm font-bold capitalize text-gray-900 dark:text-white">
                {phase.id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500">{phase.timeline}</p>
            </div>
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              {phase.initiatives.length} initiative{phase.initiatives.length !== 1 ? 's' : ''}
            </span>
          </div>

          {phase.initiatives.map((init, ii) => {
            const qs = QUADRANT_STYLES[init.quadrant.quadrant] || QUADRANT_STYLES['defer-accept']
            const priorityColor = PRIORITY_COLORS[init.priority] || '#94A3B8'
            return (
              <div
                key={init.id}
                className={`px-5 py-3 ${ii > 0 ? 'border-t border-gray-100 dark:border-gray-700/50' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: qs.color }}
                  />
                  <span className="flex-1 text-sm font-medium min-w-0 text-gray-900 dark:text-white">
                    {init.name}
                  </span>
                  {init.categoryId === 'cyber-insurance' && (
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      title="Insurance — discuss with broker around renewal"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Insurance
                    </span>
                  )}
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: priorityColor + '12', color: priorityColor }}
                  >
                    {init.priority}
                  </span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: qs.bg, color: qs.color }}
                  >
                    {qs.label}
                  </span>
                  {showCOI && (
                    <span className="text-xs text-right flex-shrink-0 w-24 font-semibold text-red-500">
                      ${init.costOfInaction.annualizedCost?.toLocaleString() || '-'}/yr
                    </span>
                  )}
                </div>
                {publishedAI?.initiativeMap?.[init.id]?.recommendation && (
                  <p className="text-xs mt-1.5 ml-5 text-gray-500 dark:text-gray-400">
                    {publishedAI.initiativeMap[init.id].recommendation}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      ))}

      {/* Priority view — sorted by net value */}
      {planView === 'priority' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="px-5 py-3.5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">All Initiatives by Priority</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">Sorted by risk reduction value</p>
          </div>
          {[...plan.initiatives]
            .sort((a, b) => b.netValue - a.netValue)
            .map((init, ii) => {
              const qs = QUADRANT_STYLES[init.quadrant.quadrant] || QUADRANT_STYLES['defer-accept']
              return (
                <div
                  key={init.id}
                  className={`px-5 py-3 flex items-center gap-3 ${ii > 0 ? 'border-t border-gray-100 dark:border-gray-700/50' : ''}`}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: qs.color }} />
                  <span className="flex-1 text-sm font-medium min-w-0 text-gray-900 dark:text-white">{init.name}</span>
                  {init.categoryId === 'cyber-insurance' && (
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300"
                      title="Insurance — discuss with broker around renewal"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Insurance
                    </span>
                  )}
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: qs.bg, color: qs.color }}>
                    {qs.label}
                  </span>
                  {showCOI && (
                    <span className={`text-xs font-semibold text-right w-28 flex-shrink-0 ${init.netValue >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {init.netValueFormatted}
                    </span>
                  )}
                </div>
              )
            })}
        </div>
      )}

      {/* Service tier + timeline context */}
      <div className="rounded-xl p-4 flex items-center gap-3 bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800">
        <svg className="w-5 h-5 flex-shrink-0 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {plan.summary.serviceTier.label} · {plan.summary.timeHorizon.label}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {plan.summary.serviceTier.advisorInvolvement}. Your plan will be reviewed and updated at each engagement checkpoint.
          </p>
        </div>
      </div>
    </div>
  )
}

export default PortalPlan
